const apiUrl = process.env.REACT_APP_API_URL;

export interface PricingTier {
  min_qty: number;
  max_qty: number | null;
  unit_price_gbp: number;
}

// Used only if the /api/pricing fetch fails, so checkout pages never break on a network hiccup.
export const FALLBACK_PRICING_TIERS: PricingTier[] = [
  { min_qty: 1, max_qty: 1, unit_price_gbp: 32.50 },
  { min_qty: 2, max_qty: 5, unit_price_gbp: 24.00 },
  { min_qty: 6, max_qty: null, unit_price_gbp: 19.50 },
];

export function calculateUnitPrice(totalItems: number, tiers: PricingTier[]): number {
  const tier = tiers.find(t => totalItems >= t.min_qty && (t.max_qty === null || totalItems <= t.max_qty));
  return tier ? tier.unit_price_gbp : (tiers[tiers.length - 1]?.unit_price_gbp ?? 0);
}

export const api = {
  generateNames: async (prompt: string) => {
    const response = await fetch(`${apiUrl}/api/generate-names`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt })
    });
    if (!response.ok) throw new Error('Failed to generate names');
    return response.json();
  },

  generateSummary: async (prompt: string) => {
    const response = await fetch(`${apiUrl}/api/generate-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt })
    });
    if (!response.ok) throw new Error('Failed to generate summary');
    return response.json();
  },

  generateLogo: async (prompt: string, teamName: string, count: number = 2) => {
    const response = await fetch(`${apiUrl}/api/generate-logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, team_name: teamName, count })
    });
    if (!response.ok) throw new Error('Failed to generate logo');
    return response.json();
  },

  refineLogo: async (feedback: string, base64Image: string) => {
    const response = await fetch(`${apiUrl}/api/refine-logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedback, base64_image: base64Image })
    });
    if (!response.ok) {
      if (response.status === 400) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.detail || 'Refinement request was blocked.');
      }
      throw new Error('Failed to refine logo');
    }
    return response.json();
  },

  validateLogo: async (base64Image: string): Promise<{ valid: boolean }> => {
    const response = await fetch(`${apiUrl}/api/validate-logo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64_image: base64Image }),
    });
    if (!response.ok) throw new Error('Failed to validate logo');
    return response.json();
  },

  moderateText: async (text: string): Promise<{ flagged: boolean }> => {
    const response = await fetch(`${apiUrl}/api/moderate-text`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!response.ok) throw new Error('Failed to moderate text');
    return response.json();
  },

  getStats: async (): Promise<{ tribes: number; kits: number; players: number; orders: number }> => {
    const response = await fetch(`${apiUrl}/api/stats`);
    if (!response.ok) throw new Error('Failed to fetch stats');
    return response.json();
  },

  getPricing: async (): Promise<{ currency: string; tiers: PricingTier[] }> => {
    const response = await fetch(`${apiUrl}/api/pricing`);
    if (!response.ok) throw new Error('Failed to fetch pricing');
    return response.json();
  },

  createCheckoutSession: async (payload: object, accessToken: string): Promise<{ checkout_url: string; session_id: string }> => {
    const response = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.detail || 'Failed to create checkout session');
    }
    return response.json();
  },
};